import type { Consumer, Subscription } from '@rails/actioncable';

import type { ChannelIdentifier } from './identifier';

type EventHandler = (data: unknown) => void;
type LifecycleCallback = () => void;

interface ManagedSubscription {
  subscription: Subscription;
  eventHandlers: Map<string, Set<EventHandler>>;
  connectedCallbacks: Set<LifecycleCallback>;
  disconnectedCallbacks: Set<LifecycleCallback>;
  rejectedCallbacks: Set<LifecycleCallback>;
  refCount: number;
  isConnected: boolean;
}

/**
 * Module-level registry of Action Cable subscriptions, keyed by JSON-serialized
 * identifier. Multiple hooks can register event handlers on the same underlying
 * subscription — only the first creates the actual `consumer.subscriptions.create()`.
 *
 * This mirrors how Pusher works: `pusher.subscribe(channel)` returns the same
 * channel object when called multiple times, and `.bind(event, handler)` just
 * adds a listener. Without this registry, each React hook would send a separate
 * `subscribe` command, which anycable-go rejects as "already subscribed".
 */
const registry = new Map<string, ManagedSubscription>();

/**
 * Produce a stable cache key regardless of property insertion order.
 * JSON.stringify output depends on key order, so two objects like
 * `{channel:"X", practice_id:"1"}` and `{practice_id:"1", channel:"X"}`
 * would generate different strings. Sorting keys makes the key deterministic.
 */
function identifierKey(identifier: ChannelIdentifier): string {
  return JSON.stringify(identifier, Object.keys(identifier).sort());
}

/**
 * Subscribe to an Action Cable channel, reusing an existing subscription if one
 * exists for the same identifier. Returns a cleanup function that removes the
 * handler and decrements the refcount; the actual subscription is only
 * unsubscribed when the last consumer cleans up.
 */
export function addSubscription(
  consumer: Consumer,
  identifier: ChannelIdentifier,
  eventName: string,
  handler: EventHandler,
  callbacks: {
    onConnected?: LifecycleCallback;
    onDisconnected?: LifecycleCallback;
    onRejected?: LifecycleCallback;
  },
): () => void {
  const key = identifierKey(identifier);

  let managed = registry.get(key);
  if (!managed) {
    managed = {
      subscription: null as unknown as Subscription,
      eventHandlers: new Map(),
      connectedCallbacks: new Set(),
      disconnectedCallbacks: new Set(),
      rejectedCallbacks: new Set(),
      refCount: 0,
      isConnected: false,
    };

    const m = managed;
    const sub = consumer.subscriptions.create(identifier, {
      connected() {
        m.isConnected = true;
        m.connectedCallbacks.forEach(cb => cb());
      },
      received(data: unknown) {
        const msg = data as Record<string, unknown>;
        const event = msg.event as string;
        if (!event) {
          // eslint-disable-next-line no-console
          console.warn(
            '[subscriptionRegistry] Received message without event property:',
            data,
          );
          return;
        }
        const handlers = m.eventHandlers.get(event);
        if (handlers) {
          const payload = msg.data ?? data;
          handlers.forEach(h => h(payload));
        } else {
          // eslint-disable-next-line no-console
          console.warn(
            `[subscriptionRegistry] No handler for event "${event}" on ${key}:`,
            data,
          );
        }
      },
      rejected() {
        m.isConnected = false;
        m.rejectedCallbacks.forEach(cb => cb());
      },
      disconnected() {
        m.isConnected = false;
        m.disconnectedCallbacks.forEach(cb => cb());
      },
    });
    managed.subscription = sub;
    registry.set(key, managed);
  }

  managed.refCount++;

  let handlers = managed.eventHandlers.get(eventName);
  if (!handlers) {
    handlers = new Set();
    managed.eventHandlers.set(eventName, handlers);
  }
  handlers.add(handler);

  if (callbacks.onConnected) {
    managed.connectedCallbacks.add(callbacks.onConnected);
  }
  if (callbacks.onDisconnected) {
    managed.disconnectedCallbacks.add(callbacks.onDisconnected);
  }
  if (callbacks.onRejected) {
    managed.rejectedCallbacks.add(callbacks.onRejected);
  }

  // If the subscription is already connected, fire connected immediately
  // so late-joiners get the correct isSubscribed state.
  if (managed.isConnected && callbacks.onConnected) {
    callbacks.onConnected();
  }

  const onConnected = callbacks.onConnected;
  const onDisconnected = callbacks.onDisconnected;
  const onRejected = callbacks.onRejected;

  return () => {
    const m = registry.get(key);
    if (!m) {
      return;
    }

    m.eventHandlers.get(eventName)?.delete(handler);
    if (m.eventHandlers.get(eventName)?.size === 0) {
      m.eventHandlers.delete(eventName);
    }

    if (onConnected) {
      m.connectedCallbacks.delete(onConnected);
    }
    if (onDisconnected) {
      m.disconnectedCallbacks.delete(onDisconnected);
    }
    if (onRejected) {
      m.rejectedCallbacks.delete(onRejected);
    }

    m.refCount--;
    if (m.refCount <= 0) {
      m.subscription.unsubscribe();
      registry.delete(key);
    }
  };
}

/**
 * Look up the shared subscription for a given identifier (for `perform` calls).
 */
export function getSubscription(
  identifier: ChannelIdentifier,
): Subscription | null {
  const managed = registry.get(identifierKey(identifier));
  return managed?.subscription ?? null;
}

/**
 * Unsubscribe and remove all managed subscriptions.
 * Called by `disconnectAnycable()` when tearing down the consumer.
 */
export function clearAllSubscriptions(): void {
  registry.forEach(m => m.subscription.unsubscribe());
  registry.clear();
}
